import debug from 'debug'

const log = debug('s4l:monitoring:circuitBreaker')

export interface CircuitBreakerConfig {
  maxEventsPerMinute?: number
  windowMs?: number
  cooldownMs?: number
}

export interface CircuitBreakerStatus {
  isOpen: boolean
  eventCount: number
  remainingCooldownMs: number
}

export class CircuitBreaker {
  private eventCount = 0
  private windowStart: number = Date.now()
  private circuitOpen = false
  private circuitOpenedAt = 0
  private readonly maxEventsPerMinute: number
  private readonly windowMs: number
  private readonly cooldownMs: number

  static create(config: CircuitBreakerConfig = {}) {
    return new CircuitBreaker(config)
  }

  constructor({
    maxEventsPerMinute = 100, // High default to not affect tests
    windowMs = 60000, // 1 minute window
    cooldownMs = 120000, // 2 minutes cooldown
  }: CircuitBreakerConfig = {}) {
    this.maxEventsPerMinute = maxEventsPerMinute
    this.windowMs = windowMs
    this.cooldownMs = cooldownMs
  }

  shouldAcceptEvent(): boolean {
    const now = Date.now()

    // If circuit is open, check if we can close it
    if (this.circuitOpen) {
      const timeSinceOpened = now - this.circuitOpenedAt

      if (timeSinceOpened < this.cooldownMs) {
        // Still in cooldown period
        return false
      }

      // Close the circuit
      log('[CircuitBreaker] Closing circuit after cooldown')
      this.circuitOpen = false
      this.eventCount = 0
      this.windowStart = now
    }

    // Reset window if it has expired
    if (now - this.windowStart >= this.windowMs) {
      this.eventCount = 0
      this.windowStart = now
    }

    // Increment event counter
    this.eventCount += 1

    // Check if we exceed the limit
    if (this.eventCount > this.maxEventsPerMinute) {
      if (!this.circuitOpen) {
        this.circuitOpen = true
        this.circuitOpenedAt = now
        log(
          `[CircuitBreaker] Circuit OPENED - Too many events (${this.eventCount}/${this.maxEventsPerMinute}). ` +
            `Cooling down for ${this.cooldownMs / 1000}s.`
        )
      }
      return false
    }

    return true
  }

  isOpen(): boolean {
    return this.circuitOpen
  }

  getStatus(): CircuitBreakerStatus {
    const now = Date.now()
    const remainingCooldownMs = this.circuitOpen
      ? Math.max(0, this.cooldownMs - (now - this.circuitOpenedAt))
      : 0

    return {
      isOpen: this.circuitOpen,
      eventCount: this.eventCount,
      remainingCooldownMs,
    }
  }
}
